import {
  Alert, Box, Button, Checkbox, Chip, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { TestSelectionWithCounts } from '../../components/shared/TestSelectionWithCounts';
import { priorities, sampleTypes } from '../../constants/statusOptions';
import { ROUTES } from '../../constants/routes';
import { getProjectByProjectId } from '../../services/projectService';
import { getLocationsByProject } from '../../services/locationService';
import { getSamplesByLocation } from '../../services/sampleService';
import { getTests, type TestType } from '../../services/testService';
import { createWorkOrder, addSamplesToWorkOrder } from '../../services/workOrderService';
import type { Sample } from '../../types/location';

interface SampleRow extends Sample { LocationId: string }

interface QueueEntry {
  id: number;
  sampleKeys: string[];
  testKeys: string[];
  priority: string;
  instructions: string;
  specimenCounts: Record<string, number>;
  // Display info
  sampleLabels: string[];
  testLabels: string[];
}

let nextQueueId = 1;

export const CreateWorkOrder = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { canCreateWorkOrder } = usePermissions();

  const preSelectedKeys: string[] = location.state?.sampleKeys ?? [];

  const [projectKey, setProjectKey] = useState('');
  const [projectName, setProjectName] = useState('');
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [tests, setTests] = useState<TestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSamples, setSelectedSamples] = useState<Set<string>>(new Set(preSelectedKeys));
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [priority, setPriority] = useState('P2');
  const [instructions, setInstructions] = useState('');
  const [specimenCounts, setSpecimenCounts] = useState<Record<string, number>>({});
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      const project = await getProjectByProjectId(projectId);
      const [locations, testList] = await Promise.all([
        getLocationsByProject(project.ProjectKey),
        getTests(),
      ]);
      setProjectKey(project.ProjectKey);
      setProjectName(project.ProjectName);
      setTests(testList);
      const allSamples: SampleRow[] = [];
      for (const loc of locations) {
        const locSamples = await getSamplesByLocation(loc.LocationKey);
        locSamples.forEach((s) => allSamples.push({ ...s, LocationId: loc.LocationId }));
      }
      setSamples(allSamples);
      setLoading(false);
    };
    load();
  }, [projectId]);

  const locationOptions = useMemo(() => [...new Set(samples.map((s) => s.LocationId))], [samples]);

  const filtered = useMemo(() => {
    return samples.filter((s) => {
      if (locationFilter && s.LocationId !== locationFilter) return false;
      if (typeFilter && s.SampleType !== typeFilter) return false;
      if (search && !s.SampleId.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [samples, locationFilter, typeFilter, search]);

  const toggleSample = (key: string) => {
    setSelectedSamples((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleTest = (key: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Add current selection to the queue (don't navigate)
  const handleAddToQueue = () => {
    if (selectedSamples.size === 0 || selectedTests.size === 0) return;
    const sampleLabels = samples
      .filter((s) => selectedSamples.has(s.SampleKey))
      .map((s) => `${s.SampleId} (${s.LocationId})`);
    const testLabels = tests
      .filter((t) => selectedTests.has(t.TestKey))
      .map((t) => t.TestName);

    setQueue((prev) => [...prev, {
      id: nextQueueId++,
      sampleKeys: Array.from(selectedSamples),
      testKeys: Array.from(selectedTests),
      priority,
      instructions,
      specimenCounts: { ...specimenCounts },
      sampleLabels,
      testLabels,
    }]);

    // Reset right panel for next batch (keep samples selected for convenience)
    setSelectedTests(new Set());
    setInstructions('');
    setSpecimenCounts({});
  };

  const removeFromQueue = (id: number) => {
    setQueue((prev) => prev.filter((e) => e.id !== id));
  };

  // Create WO with all queued entries
  const handleFinalize = useCallback(async () => {
    if (queue.length === 0 || !projectKey) return;
    setCreating(true);
    setError('');
    try {
      // First entry creates the work order
      const first = queue[0];
      const wo = await createWorkOrder(projectKey, {
        sampleKeys: first.sampleKeys,
        testKeys: first.testKeys,
        priority: first.priority,
        instructions: first.instructions,
        specimenCounts: first.specimenCounts,
      });

      // Remaining entries add to the same WO
      for (let i = 1; i < queue.length; i++) {
        const entry = queue[i];
        await addSamplesToWorkOrder(
          String(wo.WorkOrderKey),
          entry.sampleKeys,
          entry.testKeys,
          entry.specimenCounts,
        );
      }

      navigate(ROUTES.WORK_ORDER_DETAIL
        .replace(':projectId', projectId!)
        .replace(':workOrderId', String(wo.WorkOrderKey)));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to create work order.');
    } finally {
      setCreating(false);
    }
  }, [queue, projectKey, projectId, navigate]);

  const canAdd = selectedSamples.size > 0 && selectedTests.size > 0;
  const totalAssignments = queue.reduce((sum, e) => sum + e.sampleLabels.length * e.testLabels.length, 0);

  const breadcrumbs = [
    { label: 'Projects', path: ROUTES.PROJECTS },
    { label: projectName || '...', path: ROUTES.LOCATIONS.replace(':projectId', projectId!) },
    { label: 'Create Work Order' },
  ];

  if (!canCreateWorkOrder) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography>You don&apos;t have permission to create work orders.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}><Breadcrumb items={breadcrumbs} /></Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Create Work Order</Typography>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Panel — Select Samples */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>1. Select Samples</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Location</InputLabel>
              <Select value={locationFilter} label="Location" onChange={(e) => setLocationFilter(e.target.value)}>
                <MenuItem value="">All Locations</MenuItem>
                {locationOptions.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="">All Types</MenuItem>
                {sampleTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" placeholder="Search Sample Reference" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: theme.table.headerBg, fontWeight: 600 } }}>
                  <TableCell padding="checkbox" />
                  <TableCell>Sample Reference</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Depth</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
                ) : filtered.map((s) => (
                  <TableRow key={s.SampleKey} hover sx={{ height: theme.table.rowHeight }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedSamples.has(s.SampleKey)} onChange={() => toggleSample(s.SampleKey)} />
                    </TableCell>
                    <TableCell>{s.SampleId}</TableCell>
                    <TableCell>{s.LocationId}</TableCell>
                    <TableCell>{s.DepthTop ?? ''}</TableCell>
                    <TableCell>{s.SampleType}</TableCell>
                    <TableCell>{s.Status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Right Panel — Assign Tests */}
        <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>2. Assign Tests</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedSamples.size} sample{selectedSamples.size !== 1 ? 's' : ''} selected
          </Typography>

          <Box sx={{ mb: 2 }}>
            <TestSelectionWithCounts
              tests={tests}
              selectedTests={selectedTests}
              specimenCounts={specimenCounts}
              onToggleTest={toggleTest}
              onCountChange={(testKey, count) => setSpecimenCounts((prev) => ({ ...prev, [testKey]: count }))}
              label="Select Tests to Assign"
            />
          </Box>

          <FormControl size="small" sx={{ mb: 2, maxWidth: 200 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
              {priorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField label="Instructions" multiline rows={2} value={instructions}
            onChange={(e) => setInstructions(e.target.value)} sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" disabled={!canAdd} onClick={handleAddToQueue}>
              + Add to Work Order
            </Button>
            <Button onClick={() => { setSelectedSamples(new Set()); setSelectedTests(new Set()); }}>
              Clear Selection
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Queue — shows all added test assignments */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6">
            Work Order Queue
            {queue.length > 0 && (
              <Typography component="span" sx={{ ml: 1, fontSize: '0.85rem', color: 'text.secondary', fontWeight: 400 }}>
                {queue.length} batch{queue.length !== 1 ? 'es' : ''} · {totalAssignments} test assignment{totalAssignments !== 1 ? 's' : ''}
              </Typography>
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            disabled={queue.length === 0 || creating || !canCreateWorkOrder}
            onClick={handleFinalize}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {creating ? 'Creating…' : `Create Work Order (${queue.length} batch${queue.length !== 1 ? 'es' : ''})`}
          </Button>
        </Box>

        {queue.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Select samples and tests above, then click "+ Add to Work Order" to queue them here.
              You can add multiple batches with different sample/test combinations before creating.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: alpha(theme.table.headerBg, 0.5), fontWeight: 600 } }}>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Samples</TableCell>
                  <TableCell>Tests</TableCell>
                  <TableCell sx={{ width: 80 }}>Priority</TableCell>
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {queue.map((entry, i) => (
                  <TableRow key={entry.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {entry.sampleLabels.map((l) => (
                          <Chip key={l} label={l} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {entry.testLabels.map((l) => (
                          <Chip key={l} label={l} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{entry.priority}</TableCell>
                    <TableCell>
                      <Tooltip title="Remove from queue">
                        <IconButton size="small" onClick={() => removeFromQueue(entry.id)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} variant="filled" sx={{ minWidth: 200 }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};
